/**
 * The vertical list of Speedies inside the player overlay (F-129; A plan §4.4).
 *
 * Moving: swipe on a phone; arrow keys, j/k or the wheel on desktop (one step per 60 px of wheel,
 * then locked for 450 ms so a trackpad flick moves one Speedy). Keys typed into a field are
 * ignored. Only the previous, current and next slides are mounted.
 *
 * Controls (all ≥ 44 px, all labelled): close, sound, save, details, and a menu with Report (never
 * on your own Speedy). The safety icon shows when the Speedy has a high-risk category and opens the
 * details at Safety. The current title is announced when it changes.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Bookmark, BookmarkCheck, Info, MoreHorizontal, Volume2, VolumeX, X } from 'lucide-react';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';
import { useToggleSpeedySaved } from '@/hooks/use-speedies';
import type { SpeedyCard } from '@/api/speedies';
import { SpeedySlide } from './SpeedySlide';
import { SpeedyDetailsPanel, type DetailsTarget } from './SpeedyDetailsPanel';
import { SpeedyReportSheet } from './SpeedyReportSheet';
import { SpeedyCelebration } from './SpeedyCelebration';
import { useSpeedyAheadPrefetch } from './speedy-prefetch';
import { currentSpeedyVideo, SCRUB_STEP_S, SpeedyScrubber } from './SpeedyScrubber';

export interface ReelCounters {
  viewed: Set<string>;
  completed: Set<string>;
  skips: number;
}

interface SpeedyReelProps {
  items: SpeedyCard[];
  startIndex: number;
  /** More can be fetched (the feed); asked when 3 from the end. */
  hasMore: boolean;
  loadMore: () => void;
  loadingMore: boolean;
  /** The last request for more failed: don't ask again until the viewer moves. */
  loadMoreFailed?: boolean;
  counters: ReelCounters;
  celebrate?: { kind: 'series' | 'group'; name: string };
  onClose: () => void;
  onGo: (target: DetailsTarget) => void;
  /** Set while the menu or the details panel is open, so Escape closes that first. */
  detailsOpenRef: React.MutableRefObject<(() => void) | null>;
}

/** Sound stays on for the session once a member turns it on. */
let sessionUnmuted = false;

const WHEEL_STEP = 60;
const WHEEL_LOCK_MS = 450;
const SWIPE_MIN = 60;

function isTypingTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
}

export function SpeedyReel({
  items,
  startIndex,
  hasMore,
  loadMore,
  loadingMore,
  loadMoreFailed = false,
  counters,
  celebrate,
  onClose,
  onGo,
  detailsOpenRef,
}: SpeedyReelProps) {
  const isMobile = useIsMobile();
  const [index, setIndex] = useState(() => Math.min(Math.max(startIndex, 0), Math.max(items.length - 1, 0)));
  const [muted, setMuted] = useState(!sessionUnmuted);
  const [details, setDetails] = useState<{ focus?: 'safety' } | null>(null);
  const [reporting, setReporting] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [celebrating, setCelebrating] = useState(false);
  const reach = useRef(new Map<string, number>());
  const save = useToggleSpeedySaved();
  const rootRef = useRef<HTMLDivElement>(null);

  const current = items[index] as (SpeedyCard & { saved_by_me?: boolean }) | undefined;
  const ownClip = !!current?.local_clip_url;
  // The posters and preview clips of the next 20, saved while the signal is good.
  useSpeedyAheadPrefetch(items, index);

  // Saved state lives here: the list may be a snapshot the caller passed in, which no cache
  // update reaches. Set on tap, rolled back if the save fails.
  const [savedOverride, setSavedOverride] = useState<Map<string, boolean>>(() => new Map());
  const isSaved = (c: SpeedyCard & { saved_by_me?: boolean }) => savedOverride.get(c.id) ?? !!c.saved_by_me;
  const toggleSaved = (c: SpeedyCard & { saved_by_me?: boolean }) => {
    const next = !isSaved(c);
    setSavedOverride((m) => new Map(m).set(c.id, next));
    save.mutate(
      { speedyId: c.id, saved: next },
      {
        onError: () => {
          setSavedOverride((m) => new Map(m).set(c.id, !next));
          toast.error("Couldn't save. Try again.");
        },
      },
    );
  };

  // Escape closes the menu, then the details, before the player (the overlay asks through this ref).
  useEffect(() => {
    detailsOpenRef.current = menuOpen ? () => setMenuOpen(false) : details ? () => setDetails(null) : null;
    return () => {
      detailsOpenRef.current = null;
    };
  }, [details, menuOpen, detailsOpenRef]);

  const indexRef = useRef(index);
  indexRef.current = index;
  const go = useCallback((delta: number) => {
    const i = indexRef.current;
    const next = Math.min(Math.max(i + delta, 0), items.length - 1);
    if (next === i) return;
    // Counted here, not in a state updater (React may run updaters twice).
    const leaving = items[i];
    if (leaving && !counters.completed.has(leaving.id) && (reach.current.get(leaving.id) ?? 0) < 0.6) {
      counters.skips += 1;
    }
    indexRef.current = next;
    setMenuOpen(false);
    setIndex(next);
  }, [items, counters]);

  // Ask for more when 3 from the end. After a failure, only once the viewer moves again (no loop).
  const failedAtIndex = useRef<number | null>(null);
  useEffect(() => {
    if (loadMoreFailed && failedAtIndex.current === null) failedAtIndex.current = index;
    if (!loadMoreFailed) failedAtIndex.current = null;
  }, [loadMoreFailed, index]);
  useEffect(() => {
    if (!hasMore || loadingMore || index < items.length - 3) return;
    if (loadMoreFailed && failedAtIndex.current === index) return;
    loadMore();
  }, [index, items.length, hasMore, loadingMore, loadMore, loadMoreFailed]);

  // A list that got shorter never leaves the reel on nothing.
  useEffect(() => {
    if (items.length > 0 && index > items.length - 1) setIndex(items.length - 1);
  }, [items.length, index]);

  // Keys.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target) || reporting || celebrating) return;
      if (e.key === 'ArrowDown' || e.key === 'j') {
        e.preventDefault();
        go(1);
      } else if (e.key === 'ArrowUp' || e.key === 'k') {
        e.preventDefault();
        go(-1);
      } else if (e.key === 'm') {
        toggleMuteRef.current();
      } else if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && !(e.target as HTMLElement | null)?.closest?.('[role="slider"]')) {
        // ← / → move 5 seconds through the Speedy on screen (the progress bar handles its own keys).
        const v = currentSpeedyVideo(rootRef.current);
        if (!v || !Number.isFinite(v.duration)) return;
        e.preventDefault();
        v.currentTime = Math.min(v.duration, Math.max(0, v.currentTime + (e.key === 'ArrowRight' ? SCRUB_STEP_S : -SCRUB_STEP_S)));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, reporting, celebrating]);

  // Wheel: non-passive so the page behind never scrolls.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    let sum = 0;
    let lockedUntil = 0;
    const onWheel = (e: WheelEvent) => {
      if (details || reporting || celebrating) return;
      e.preventDefault();
      const now = Date.now();
      if (now < lockedUntil) return;
      sum += e.deltaY;
      if (Math.abs(sum) >= WHEEL_STEP) {
        go(sum > 0 ? 1 : -1);
        sum = 0;
        lockedUntil = now + WHEEL_LOCK_MS;
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [go, details, reporting, celebrating]);

  // Swipe.
  const touchY = useRef<number | null>(null);
  const onTouchStart = (e: React.TouchEvent) => {
    // No swipe while the details panel, menu or report sheet is open, or from inside the panel.
    // React passes touches inside a portal (the report drawer) to this handler too.
    const fromPanel = (e.target as HTMLElement | null)?.closest?.('[data-speedy-no-swipe]');
    touchY.current = details || reporting || menuOpen || celebrating || fromPanel ? null : e.touches[0]?.clientY ?? null;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const start = touchY.current;
    touchY.current = null;
    const end = e.changedTouches[0]?.clientY;
    if (start === null || end === undefined) return;
    const dy = start - end;
    if (Math.abs(dy) >= SWIPE_MIN) go(dy > 0 ? 1 : -1);
  };

  const onEnded = useCallback((endedId?: string) => {
    const i = indexRef.current;
    // Only the slide on screen moves the reel.
    if (endedId && items[i]?.id !== endedId) return;
    if (i < items.length - 1) {
      go(1);
    } else if (
      celebrate && !hasMore &&
      // The one that just ended counts as watched: its completion answer can arrive after `ended`.
      items.every((c) => c.id === endedId || counters.completed.has(c.id) || (c as { finished_by_me?: boolean }).finished_by_me)
    ) {
      setCelebrating(true);
    }
    // Otherwise the last Speedy stays at its end (or loops, see `loop` below).
  }, [items, go, celebrate, hasMore, counters]);

  const onCompleted = useCallback((id: string, points: number) => {
    if (counters.completed.has(id)) return;
    counters.completed.add(id);
    if (points > 0) toast.success(`+${points} points`);
  }, [counters]);

  // Sound on/off. Set on the video itself inside the tap as well: iOS only lets sound start from
  // the gesture, and the slide's own effect runs after it.
  const toggleMute = useCallback(() => {
    setMuted((m) => {
      const next = !m;
      if (!next) sessionUnmuted = true;
      rootRef.current?.querySelectorAll<HTMLVideoElement>('[data-speedy-current] video').forEach((v) => {
        v.muted = next;
      });
      return next;
    });
  }, []);
  const toggleMuteRef = useRef(toggleMute);
  toggleMuteRef.current = toggleMute;

  const window3 = useMemo(() => [index - 1, index, index + 1].filter((i) => i >= 0 && i < items.length), [index, items.length]);

  if (!current) {
    // Nothing to show (an empty or emptied list): still a way out.
    return (
      <div className="flex h-full items-center justify-center">
        <button type="button" onClick={onClose} className="min-h-[44px] rounded-full bg-white px-5 text-sm font-semibold text-black">Close</button>
      </div>
    );
  }

  return (
    <div ref={rootRef} className="relative flex h-full w-full overflow-hidden overscroll-none" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="relative h-full flex-1">
        {window3.map((i) => (
          <div
            key={items[i].id}
            className="absolute inset-0 transition-transform duration-300 motion-reduce:transition-none"
            // A band of SLIDE_GAP_PX between Speedies (Tim, 3 Nov: "I can't tell I'm moving between them"):
            // it shows as a line passing up the screen during a swipe.
            style={{ transform: `translateY(calc(${(i - index) * 100}% + ${(i - index) * SLIDE_GAP_PX}px))` }}
            aria-hidden={i !== index}
            data-speedy-current={i === index ? '' : undefined}
          >
            {i < items.length - 1 && <SlideDivider />}
            <SpeedySlide
              card={items[i]}
              role={i === index ? 'current' : i < index ? 'prev' : 'next'}
              muted={muted}
              loop={i === items.length - 1 && !hasMore && !celebrate}
              suspended={reporting || celebrating}
              onViewed={(id) => counters.viewed.add(id)}
              onCompleted={onCompleted}
              onReach={(id, f) => reach.current.set(id, f)}
              onEnded={onEnded}
            />
          </div>
        ))}

        {/* Top controls */}
        <div className="absolute inset-x-0 top-0 flex items-center justify-between p-2 pt-[max(env(safe-area-inset-top),0.5rem)]">
          <button type="button" onClick={onClose} className="flex h-11 w-11 items-center justify-center rounded-full bg-black/40 text-white" aria-label="Close player">
            <X className="h-5 w-5" aria-hidden />
          </button>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={toggleMute}
              className="flex h-11 items-center gap-1.5 rounded-full bg-black/40 px-3 text-xs font-semibold text-white"
              aria-label={muted ? 'Turn sound on' : 'Turn sound off'}
            >
              {muted ? <VolumeX className="h-5 w-5" aria-hidden /> : <Volume2 className="h-5 w-5" aria-hidden />}
              {muted && <span>Tap for sound</span>}
            </button>
            {!ownClip && <div className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen((o) => !o)}
                className="flex h-11 w-11 items-center justify-center rounded-full bg-black/40 text-white"
                aria-label="More"
                aria-expanded={menuOpen}
              >
                <MoreHorizontal className="h-5 w-5" aria-hidden />
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-12 z-10 min-w-[160px] rounded-lg bg-card p-1 text-sm text-foreground shadow-lg" role="menu">
                  <button type="button" role="menuitem" className="flex min-h-[44px] w-full items-center px-3 text-left hover:bg-secondary" onClick={() => { setDetails({}); setMenuOpen(false); }}>
                    Details
                  </button>
                  {!current.is_mine && !!current.author?.id && (
                    <button type="button" role="menuitem" className="flex min-h-[44px] w-full items-center px-3 text-left hover:bg-secondary" onClick={() => { setReporting(true); setMenuOpen(false); }}>
                      Report
                    </button>
                  )}
                </div>
              )}
            </div>}
          </div>
        </div>

        {/* Right rail: none for the member's own clip (it has no details, and isn't saveable yet). */}
        {!ownClip && <div className="absolute bottom-28 right-2 flex flex-col items-center gap-2">
          {current.has_safety_risk && (
            <button
              type="button"
              onClick={() => setDetails({ focus: 'safety' })}
              className="flex h-11 w-11 items-center justify-center rounded-full bg-warning/90 text-black"
              aria-label="Safety: high-risk work in this Speedy"
            >
              <AlertTriangle className="h-5 w-5" aria-hidden />
            </button>
          )}
          <button
            type="button"
            disabled={save.isPending}
            onClick={() => toggleSaved(current)}
            className="flex h-11 w-11 items-center justify-center rounded-full bg-black/40 text-white disabled:opacity-60"
            aria-label={isSaved(current) ? 'Remove from saved' : 'Save'}
            aria-pressed={isSaved(current)}
          >
            {isSaved(current) ? <BookmarkCheck className="h-5 w-5" aria-hidden /> : <Bookmark className="h-5 w-5" aria-hidden />}
          </button>
          <button type="button" onClick={() => setDetails({})} className="flex h-11 w-11 items-center justify-center rounded-full bg-black/40 text-white" aria-label="Details">
            <Info className="h-5 w-5" aria-hidden />
          </button>
        </div>}

        {/* Title and author */}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4 pb-[max(env(safe-area-inset-bottom),1rem)] text-white">
          <button type="button" onClick={() => !ownClip && setDetails({})} className="block max-w-[80%] text-left">
            <p className="line-clamp-2 text-base font-semibold" aria-live="polite">{current.title ?? 'Untitled Speedy'}</p>
          </button>
          <p className="mt-1 text-xs text-white/80">
            {ownClip ? "Only you can see this until it's live" : current.author?.name ?? 'A member'}
          </p>
          {(current.shown_tags ?? []).length > 0 && (
            <p className="mt-1 line-clamp-1 text-xs text-white/70">{(current.shown_tags ?? []).map((t) => t.label).join(' · ')}</p>
          )}
          {loadingMore && index >= items.length - 1 && <p className="mt-1 text-xs text-white/60" role="status">Loading more…</p>}
          <div className="mt-2">
            <SpeedyScrubber rootRef={rootRef} speedyId={current.id} fallbackDuration={current.duration_s ?? null} />
          </div>
        </div>
      </div>

      {details && (
        <div
          data-speedy-no-swipe
          className={isMobile
            ? 'absolute inset-x-0 bottom-0 z-20 h-[70%] overflow-hidden rounded-t-2xl'
            : 'relative z-20 h-full w-[380px] shrink-0 border-l border-border'}
        >
          <SpeedyDetailsPanel
            speedyId={current.id}
            focus={details.focus}
            canReport={!current.is_mine && !!current.author?.id}
            onReport={() => setReporting(true)}
            onClose={() => setDetails(null)}
            onGo={onGo}
          />
        </div>
      )}

      {celebrating && celebrate && (
        <SpeedyCelebration
          kind={celebrate.kind}
          name={celebrate.name}
          onClose={onClose}
          onReplay={() => {
            setCelebrating(false);
            setIndex(0);
          }}
        />
      )}

      <SpeedyReportSheet open={reporting} onOpenChange={setReporting} speedyId={current.id} authorId={current.author?.id ?? ''} />
    </div>
  );
}

/** The space between two Speedies in the player. */
export const SLIDE_GAP_PX = 10;

/** The band under a Speedy, in the gap before the next one: card grey with a thin light line. */
function SlideDivider() {
  return (
    <div className="pointer-events-none absolute inset-x-0 top-full flex items-center bg-card" style={{ height: SLIDE_GAP_PX }} aria-hidden>
      <span className="h-px w-full bg-white/25" />
    </div>
  );
}
